import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe HTML elements.
 * @param dirty - The potentially unsafe HTML string to sanitize
 * @returns The sanitized HTML string safe for rendering
 */
export const sanitizeHtml = (dirty: string): string => {
  // Configure DOMPurify with safe defaults
  const config = {
    // Allow only safe HTML tags
    ALLOWED_TAGS: [
      'a', 'b', 'i', 'em', 'strong', 'u', 'p', 'br', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre'
    ],
    // Allow only safe attributes
    ALLOWED_ATTR: [
      'href', 'title', 'target', 'rel', 'class', 'id', 'style'
    ],
    // Ensure links open in new windows safely
    ADD_ATTR: ['target', 'rel'],
    // Remove dangerous elements
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    // Keep text content even if tags are removed
    KEEP_CONTENT: true,
    // Allow data: URLs for images (optional, can be removed for stricter security)
    ALLOW_DATA_ATTR: false,
    // Prevent DOM clobbering attacks
    SANITIZE_DOM: true
  };

  // Sanitize the HTML string
  const clean = DOMPurify.sanitize(dirty, config);
  
  // Additional safety: ensure all links have rel="noopener noreferrer" for external links
  const parser = new DOMParser();
  const doc = parser.parseFromString(clean, 'text/html');
  const links = doc.querySelectorAll('a[target="_blank"]');
  
  links.forEach(link => {
    link.setAttribute('rel', 'noopener noreferrer');
  });
  
  return doc.body.innerHTML;
};

/**
 * Sanitizes HTML with minimal allowed tags for error messages.
 * More restrictive than the general sanitizeHtml function.
 * @param dirty - The potentially unsafe HTML string to sanitize
 * @returns The sanitized HTML string safe for rendering
 */
export const sanitizeErrorMessage = (dirty: string): string => {
  const config = {
    // Only allow basic formatting tags for error messages
    ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'br', 'span'],
    ALLOWED_ATTR: ['href', 'title'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: false,
    SANITIZE_DOM: true
  };

  return DOMPurify.sanitize(dirty, config);
};